import { maxDebug, maxMessageDebug } from '../../utils/max-debug'
import { logUnhandledRealtimeFrame } from '../utils/realtime-diagnostics'

export type WebSocketEventType =
  | 'message:received'
  | 'message:sent'
  | 'message:deleted'
  | 'stream_start'
  | 'chunk'
  | 'stream_end'
  | 'agent_status'
  | 'conversation:created'
  | 'conversation:updated'
  | 'conversation:deleted'
  | 'conversation:selected'
  | 'typing:start'
  | 'typing:stop'
  | 'error'
  | 'connected'
  | 'disconnected'

export const WS_EVENT_TYPES = {
  MESSAGE_RECEIVED: 'message:received' as const,
  MESSAGE_SENT: 'message:sent' as const,
  MESSAGE_DELETED: 'message:deleted' as const,
  STREAM_START: 'stream_start' as const,
  STREAM_CHUNK: 'chunk' as const,
  STREAM_END: 'stream_end' as const,
  AGENT_STATUS: 'agent_status' as const,
  CONVERSATION_CREATED: 'conversation:created' as const,
  CONVERSATION_UPDATED: 'conversation:updated' as const,
  CONVERSATION_DELETED: 'conversation:deleted' as const,
  CONVERSATION_SELECTED: 'conversation:selected' as const,
  TYPING_START: 'typing:start' as const,
  TYPING_STOP: 'typing:stop' as const,
  ERROR: 'error' as const,
  CONNECTED: 'connected' as const,
  DISCONNECTED: 'disconnected' as const,
} as const

const describeEmitPayload = (
  type: WebSocketEventType,
  data: unknown
): Record<string, unknown> => {
  if (type === WS_EVENT_TYPES.MESSAGE_SENT && data && typeof data === 'object') {
    const payload = data as Record<string, unknown>
    const attachments = payload.attachments
    const prompt = payload.prompt

    return {
      action: payload.action,
      conversationId: payload.conversationId,
      tempConversationId: payload.tempConversationId,
      mode: payload.mode,
      hasToken: Boolean(payload.token),
      promptLength: typeof prompt === 'string' ? prompt.length : undefined,
      attachmentsCount: Array.isArray(attachments) ? attachments.length : 0,
    }
  }

  return {
    hasData: data !== undefined,
  }
}

const describeInboundPayload = (data: unknown): Record<string, unknown> => {
  if (!data || typeof data !== 'object') {
    return {
      payloadType: typeof data,
    }
  }

  const payload = data as Record<string, unknown>
  return {
    type: typeof payload.type === 'string' ? payload.type : undefined,
    requestId: typeof payload.requestId === 'string' ? payload.requestId : undefined,
    conversationId:
      typeof payload.conversationId === 'string' ? payload.conversationId : undefined,
    messageId: typeof payload.messageId === 'string' ? payload.messageId : undefined,
    tempConversationId:
      typeof payload.tempConversationId === 'string' ? payload.tempConversationId : undefined,
    hasResponse: typeof payload.response === 'string',
    contentLength: typeof payload.content === 'string' ? payload.content.length : undefined,
    index: typeof payload.index === 'number' ? payload.index : undefined,
  }
}

export interface WebSocketEvent<T = unknown> {
  type: WebSocketEventType
  data: T
  timestamp: number
  conversationId?: string
}

export type WebSocketEventHandler<T = unknown> = (event: WebSocketEvent<T>) => void

export interface IWebSocketService {
  connect(url: string, token?: string): Promise<void>
  disconnect(): void
  isConnected(): boolean
  subscribe<T = unknown>(eventType: WebSocketEventType, handler: WebSocketEventHandler<T>): () => void
  subscribeToConversation(conversationId: string, handler: WebSocketEventHandler): () => void
  emit<T = unknown>(type: WebSocketEventType, data: T, conversationId?: string): void
  getState(): WebSocketState
  getToken(): string | null
}

export enum WebSocketState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  CLOSED = 'closed',
}

export class WebSocketService implements IWebSocketService {
  private state: WebSocketState = WebSocketState.DISCONNECTED
  private ws: WebSocket | null = null
  private url = ''
  private token: string | null = null
  private reconnectAttempts = 0
  private readonly maxReconnectAttempts: number
  private readonly reconnectDelayBaseMs: number
  private readonly reconnectDelayMaxMs: number
  private eventHandlers: Map<string, Set<WebSocketEventHandler>> = new Map()
  private conversationHandlers: Map<string, Set<WebSocketEventHandler>> = new Map()
  private messageQueue: WebSocketEvent[] = []
  private connectionPromise: Promise<void> | null = null
  private connectionResolve: (() => void) | null = null
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
  private autoConnect: boolean

  constructor(options?: {
    autoReconnect?: boolean
    maxReconnectAttempts?: number
    reconnectDelayBaseMs?: number
    reconnectDelayMaxMs?: number
  }) {
    this.autoConnect = options?.autoReconnect ?? true
    this.maxReconnectAttempts = options?.maxReconnectAttempts ?? 5
    this.reconnectDelayBaseMs = options?.reconnectDelayBaseMs ?? 1000
    this.reconnectDelayMaxMs = options?.reconnectDelayMaxMs ?? 30000
  }

  private resolveConnectionPromise(): void {
    if (this.connectionResolve) {
      maxDebug.debug('WebSocket resolveConnectionPromise', {
        state: this.state,
      })
      this.connectionResolve()
    }
    this.connectionResolve = null
    this.connectionPromise = null
  }

  async connect(url: string, token?: string): Promise<void> {
    maxDebug.debug('WebSocket connect requested', {
      state: this.state,
      hasToken: Boolean(token),
      tokenPreview: maxDebug.token(token),
      hasExistingPromise: Boolean(this.connectionPromise),
      hasSocket: Boolean(this.ws),
    })

    if (this.state === WebSocketState.CONNECTED) {
      maxDebug.debug('WebSocket connect skipped: already connected', {
        state: this.state,
      })
      return
    }

    if (
      this.connectionPromise &&
      (this.state === WebSocketState.CONNECTING || this.state === WebSocketState.RECONNECTING)
    ) {
      maxDebug.debug('WebSocket connect reused in-flight promise', {
        state: this.state,
      })
      return this.connectionPromise
    }

    if (this.connectionPromise) {
      maxDebug.warn('WebSocket clearing stale connection promise', {
        state: this.state,
      })
      this.connectionPromise = null
      this.connectionResolve = null
    }

    this.autoConnect = true
    this.url = url
    if (token) {
      this.token = token
    }
    this.reconnectAttempts = 0
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    this.connectionPromise = new Promise((resolve) => {
      this.connectionResolve = resolve
      maxDebug.debug('WebSocket performConnect scheduled from connect()', {
        state: this.state,
        reconnectAttempts: this.reconnectAttempts,
      })
      this.performConnect()
    })

    return this.connectionPromise
  }

  disconnect(): void {
    this.autoConnect = false
    this.state = WebSocketState.CLOSED

    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    if (this.messageQueue.length > 0) {
      this.messageQueue = []
    }

    this.token = null

    const socket = this.ws
    this.ws = null
    if (socket) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
    }

    this.resolveConnectionPromise()
    this.emitEvent(WS_EVENT_TYPES.DISCONNECTED, {})
  }

  isConnected(): boolean {
    return this.state === WebSocketState.CONNECTED
  }

  subscribe<T = unknown>(
    eventType: WebSocketEventType,
    handler: WebSocketEventHandler<T>
  ): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }

    const handlers = this.eventHandlers.get(eventType)!
    handlers.add(handler as WebSocketEventHandler)

    return () => {
      handlers.delete(handler as WebSocketEventHandler)
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType)
      }
    }
  }

  subscribeToConversation(conversationId: string, handler: WebSocketEventHandler): () => void {
    if (!this.conversationHandlers.has(conversationId)) {
      this.conversationHandlers.set(conversationId, new Set())
    }

    const handlers = this.conversationHandlers.get(conversationId)!
    handlers.add(handler)

    return () => {
      handlers.delete(handler)
      if (handlers.size === 0) {
        this.conversationHandlers.delete(conversationId)
      }
    }
  }

  emit<T = unknown>(type: WebSocketEventType, data: T, conversationId?: string): void {
    let messageToSend: string
    const payloadInfo = describeEmitPayload(type, data)

    if (type === WS_EVENT_TYPES.MESSAGE_SENT && typeof data === 'object' && data !== null) {
      messageToSend = JSON.stringify(data)
    } else {
      const event: WebSocketEvent<T> = {
        type,
        data,
        timestamp: Date.now(),
        conversationId,
      }
      messageToSend = JSON.stringify(event)
    }

    if (!this.isConnected()) {
      this.messageQueue.push({
        type,
        data,
        timestamp: Date.now(),
        conversationId,
      })
      if (type === WS_EVENT_TYPES.MESSAGE_SENT) {
        maxMessageDebug.error('WebSocket emit queued because socket is not connected', {
          type,
          conversationId,
          state: this.state,
          queueLength: this.messageQueue.length,
          payloadInfo,
        })
      }
      maxDebug.warn('WebSocket emit queued: not connected', {
        type,
        conversationId,
        state: this.state,
        queueLength: this.messageQueue.length,
        ...payloadInfo,
      })
      return
    }

    try {
      if (this.ws) {
        if (type === WS_EVENT_TYPES.MESSAGE_SENT) {
          maxMessageDebug.event('WebSocket emit sent to browser socket', {
            type,
            conversationId,
            state: this.state,
            payloadInfo,
          })
        }
        maxDebug.debug('WebSocket emit send', {
          type,
          conversationId,
          state: this.state,
          ...payloadInfo,
        })
        this.ws.send(messageToSend)
      } else {
        if (type === WS_EVENT_TYPES.MESSAGE_SENT) {
          maxMessageDebug.error('WebSocket emit skipped because socket instance is missing', {
            type,
            conversationId,
            state: this.state,
          })
        }
        maxDebug.error('WebSocket emit skipped: socket missing while connected', {
          type,
          conversationId,
          state: this.state,
        })
      }
    } catch (error) {
      this.messageQueue.push({
        type,
        data,
        timestamp: Date.now(),
        conversationId,
      })
      if (type === WS_EVENT_TYPES.MESSAGE_SENT) {
        maxMessageDebug.error('WebSocket emit failed and message was queued', {
          type,
          conversationId,
          state: this.state,
          queueLength: this.messageQueue.length,
          payloadInfo,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
      }
      maxDebug.warn('WebSocket emit queued: send error', {
        type,
        conversationId,
        state: this.state,
        queueLength: this.messageQueue.length,
        ...payloadInfo,
      })
      maxDebug.error('WebSocket send threw', {
        type,
        conversationId,
        state: this.state,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    }
  }

  getState(): WebSocketState {
    return this.state
  }

  getToken(): string | null {
    return this.token
  }

  private performConnect(): void {
    try {
      this.state = WebSocketState.CONNECTING

      let wsUrl = this.url
      if (this.token) {
        const separator = this.url.includes('?') ? '&' : '?'
        wsUrl = `${this.url}${separator}token=${encodeURIComponent(this.token)}`
      }

      maxDebug.debug('WebSocket creating browser socket', {
        connectUrl: wsUrl,
        reconnectAttempts: this.reconnectAttempts,
      })

      const socket = new WebSocket(wsUrl)
      this.ws = socket

      socket.onopen = () => {
        if (this.ws !== socket) {
          maxDebug.warn('WebSocket onopen ignored: stale socket', {
            reconnectAttempts: this.reconnectAttempts,
          })
          return
        }

        this.state = WebSocketState.CONNECTED
        this.reconnectAttempts = 0
        this.flushMessageQueue()
        this.resolveConnectionPromise()
        this.emitEvent(WS_EVENT_TYPES.CONNECTED, {})
      }

      socket.onmessage = (event) => {
        if (this.ws !== socket) {
          maxDebug.warn('WebSocket onmessage ignored: stale socket', {})
          return
        }

        try {
          const parsedData = JSON.parse(event.data as string)

          let wsEvent: WebSocketEvent

          if (parsedData.response && parsedData.conversationId) {
            wsEvent = {
              type: WS_EVENT_TYPES.MESSAGE_RECEIVED,
              data: parsedData,
              timestamp: Date.now(),
              conversationId: parsedData.conversationId,
            }
          } else if (parsedData.type) {
            wsEvent = {
              type: parsedData.type as WebSocketEventType,
              data: parsedData,
              timestamp: Date.now(),
              conversationId:
                typeof parsedData.conversationId === 'string'
                  ? parsedData.conversationId
                  : undefined,
            }
          } else {
            // Unknown format: no `response` + `conversationId`, and no `type` discriminator.
            logUnhandledRealtimeFrame(
              'WebSocketService.onmessage',
              'unrecognized frame shape (needs either `response` + `conversationId`, or `type`)',
              parsedData
            )
            return
          }

          maxDebug.debug('WebSocket inbound event', {
            eventType: wsEvent.type,
            conversationId: wsEvent.conversationId,
            ...describeInboundPayload(parsedData),
          })
          this.handleEvent(wsEvent)
        } catch (error) {
          // The raw text matters more than the error here: it is the only record of what arrived.
          logUnhandledRealtimeFrame(
            'WebSocketService.onmessage',
            `JSON parse failed: ${error instanceof Error ? error.message : String(error)}`,
            event.data
          )
        }
      }

      socket.onerror = (error) => {
        if (this.ws !== socket) {
          maxDebug.warn('WebSocket onerror ignored: stale socket', {})
          return
        }

        maxDebug.error('WebSocket transport error', {
          state: this.state,
          reconnectAttempts: this.reconnectAttempts,
          readyState: socket.readyState,
          errorType: typeof error,
        })
        this.emitEvent(WS_EVENT_TYPES.ERROR, { message: 'WebSocket error occurred' })
      }

      socket.onclose = (event) => {
        if (this.ws !== socket) {
          maxDebug.warn('WebSocket onclose ignored: stale socket', {
            code: event.code,
            reason: event.reason,
          })
          return
        }

        this.ws = null
        this.state = WebSocketState.DISCONNECTED
        maxDebug.warn('WebSocket closed', {
          code: event.code,
          reason: event.reason || 'none',
          wasClean: event.wasClean,
          autoConnect: this.autoConnect,
          reconnectAttempts: this.reconnectAttempts,
          maxReconnectAttempts: this.maxReconnectAttempts,
        })

        if (this.autoConnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect()
          return
        }

        this.state = WebSocketState.CLOSED
        maxDebug.warn('WebSocket transitioned to closed', {
          code: event.code,
          reason: event.reason || 'none',
          wasClean: event.wasClean,
          autoConnect: this.autoConnect,
        })
        this.emitEvent(WS_EVENT_TYPES.DISCONNECTED, {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        })
        this.resolveConnectionPromise()
      }
    } catch (error) {
      maxDebug.error('WebSocket connection failed before open', {
        errorMessage: error instanceof Error ? error.message : String(error),
        reconnectAttempts: this.reconnectAttempts,
        autoConnect: this.autoConnect,
        baseUrl: this.url,
      })
      if (this.autoConnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect()
      } else {
        this.state = WebSocketState.CLOSED
        this.emitEvent(WS_EVENT_TYPES.ERROR, { message: 'Failed to establish WebSocket connection' })
        this.resolveConnectionPromise()
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeoutId !== null) {
      maxDebug.debug('WebSocket reconnect already scheduled', {
        reconnectAttempts: this.reconnectAttempts,
        state: this.state,
      })
      return
    }

    this.state = WebSocketState.RECONNECTING
    this.reconnectAttempts += 1

    const delay = Math.min(
      this.reconnectDelayBaseMs * Math.pow(2, this.reconnectAttempts - 1),
      this.reconnectDelayMaxMs
    )

    maxDebug.warn('WebSocket reconnect scheduled', {
      reconnectAttempts: this.reconnectAttempts,
      delayMs: delay,
      state: this.state,
      autoConnect: this.autoConnect,
      hasToken: Boolean(this.token),
      tokenPreview: maxDebug.token(this.token),
    })

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null
      if (
        this.state === WebSocketState.RECONNECTING ||
        this.state === WebSocketState.DISCONNECTED
      ) {
        maxDebug.warn('WebSocket reconnect timer firing', {
          reconnectAttempts: this.reconnectAttempts,
          state: this.state,
        })
        this.performConnect()
        return
      }
      maxDebug.warn('WebSocket reconnect timer skipped: state changed', {
        reconnectAttempts: this.reconnectAttempts,
        state: this.state,
      })
    }, delay)
  }

  private handleEvent(event: WebSocketEvent): void {
    const typeHandlers = this.eventHandlers.get(event.type)
    if (typeHandlers) {
      typeHandlers.forEach((handler) => handler(event))
    }

    if (event.conversationId) {
      const conversationHandlers = this.conversationHandlers.get(event.conversationId)
      if (conversationHandlers) {
        conversationHandlers.forEach((handler) => handler(event))
      }
    }
  }

  private emitEvent(type: WebSocketEventType, data: unknown): void {
    maxDebug.debug('WebSocket emitEvent dispatch', {
      type,
      state: this.state,
      ...describeInboundPayload(data),
    })
    const event: WebSocketEvent = {
      type,
      data,
      timestamp: Date.now(),
    }
    this.handleEvent(event)
  }

  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) {
      maxDebug.debug('WebSocket flush queue skipped: queue empty', {
        state: this.state,
      })
      return
    }

    maxDebug.warn('WebSocket flushing queued messages', {
      queuedMessages: this.messageQueue.length,
      state: this.state,
    })
    while (this.messageQueue.length > 0) {
      const event = this.messageQueue.shift()
      if (!event) continue
      this.emit(event.type, event.data, event.conversationId)
    }
  }
}
